import { useState } from 'react';
import axios from 'axios';
import { API, BACKEND_URL } from '../App';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Camera, Upload, X, ShieldCheck, Loader2, AlertCircle } from 'lucide-react';

const resolveUrl = (url) => (url?.startsWith('http') ? url : `${BACKEND_URL}${url}`);

/**
 * Reusable form section for collectible authenticity:
 * - Condition (raw / psa / bgs / cgc)
 * - Grade
 * - Cert number with "Verify" button (calls /api/verify/cert)
 * - Photo upload (multi-file, max 5)
 *
 * Props (controlled):
 *   condition, grade, certNumber: strings
 *   onChange: (patch) => void           // patch like { condition, grade, cert_number }
 *   photoFiles, setPhotoFiles           // staged files (not yet uploaded)
 *   photoUrls, setPhotoUrls             // already uploaded URLs (returned from /api/upload/photo)
 *   certVerified, setCertVerified       // {verified, note, lookup_url, details}|null
 *   showWhen: 'physical' | 'always'     // whether to render (defaults to always)
 */
export function AuthenticityFields({
  condition,
  grade,
  certNumber,
  onChange,
  photoFiles,
  setPhotoFiles,
  photoUrls,
  setPhotoUrls,
  certVerified,
  setCertVerified,
  required = false,
}) {
  const [verifying, setVerifying] = useState(false);

  const handleVerifyCert = async () => {
    if (!certNumber || !certNumber.trim()) {
      toast.error('Enter a cert number first');
      return;
    }
    if (!condition || condition === 'raw') {
      toast.error('Select a grading service (PSA/BGS/CGC) first');
      return;
    }
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/verify/cert`, {
        cert_number: certNumber.trim(),
        grading_service: condition,
      });
      setCertVerified(res.data);
      if (res.data.verified) {
        toast.success(`${condition.toUpperCase()} cert verified!`);
      } else {
        toast.warning(res.data.note || 'Cert not auto-verified — admin will review');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Verification failed');
    } finally {
      setVerifying(false);
    }
  };

  const handleFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    const total = (photoFiles?.length || 0) + (photoUrls?.length || 0) + files.length;
    if (total > 5) {
      toast.error('Max 5 photos per listing');
      return;
    }
    const valid = files.filter((f) => {
      if (!f.type.startsWith('image/')) {
        toast.error(`${f.name}: only images allowed`);
        return false;
      }
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name}: max 5MB`);
        return false;
      }
      return true;
    });
    setPhotoFiles([...(photoFiles || []), ...valid]);
    e.target.value = '';
  };

  const removeStagedFile = (idx) => {
    setPhotoFiles(photoFiles.filter((_, i) => i !== idx));
  };
  const removeUploadedUrl = (idx) => {
    setPhotoUrls(photoUrls.filter((_, i) => i !== idx));
  };

  const totalPhotos = (photoFiles?.length || 0) + (photoUrls?.length || 0);

  return (
    <div data-testid="authenticity-fields" className="space-y-2.5 bg-slate-800/30 border border-slate-700/40 rounded-xl p-3">
      <div className="flex items-center gap-1.5">
        <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
        <p className="text-[11px] font-bold text-white uppercase tracking-wide">
          Authenticity {required && <span className="text-red-400">*</span>}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Select
          value={condition || ''}
          onValueChange={(v) => {
            // Clear grade + cert when switching to raw
            if (v === 'raw') {
              onChange({ condition: v, grade: '', cert_number: '' });
            } else {
              onChange({ condition: v });
            }
            setCertVerified(null);
          }}
        >
          <SelectTrigger data-testid="auth-condition-select" className="bg-slate-800 border-slate-700 text-white h-9 text-xs">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            <SelectItem value="raw" className="text-white">Raw / Ungraded</SelectItem>
            <SelectItem value="psa" className="text-white">PSA</SelectItem>
            <SelectItem value="bgs" className="text-white">BGS</SelectItem>
            <SelectItem value="cgc" className="text-white">CGC</SelectItem>
          </SelectContent>
        </Select>

        <Input
          data-testid="auth-grade-input"
          placeholder="Grade (e.g. 10)"
          value={grade || ''}
          onChange={(e) => onChange({ grade: e.target.value })}
          disabled={!condition || condition === 'raw'}
          className="bg-slate-800 border-slate-700 text-white h-9 text-xs"
        />
      </div>

      {condition && condition !== 'raw' && (
        <div className="flex gap-2">
          <Input
            data-testid="auth-cert-input"
            placeholder={`${condition.toUpperCase()} Cert Number`}
            value={certNumber || ''}
            onChange={(e) => { onChange({ cert_number: e.target.value }); setCertVerified(null); }}
            className="bg-slate-800 border-slate-700 text-white h-9 text-xs flex-1"
          />
          <Button
            type="button"
            data-testid="auth-verify-button"
            onClick={handleVerifyCert}
            disabled={verifying || !certNumber}
            size="sm"
            className="bg-emerald-600 hover:bg-emerald-700 h-9 text-xs px-3"
          >
            {verifying ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Verify'}
          </Button>
        </div>
      )}

      {certVerified && (
        <div
          data-testid="cert-verify-result"
          className={`flex items-start gap-1.5 text-[10px] p-2 rounded-lg ${
            certVerified.verified
              ? 'bg-emerald-500/10 border border-emerald-500/25 text-emerald-300'
              : 'bg-amber-500/10 border border-amber-500/25 text-amber-300'
          }`}
        >
          {certVerified.verified ? <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> : <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
          <div>
            {certVerified.verified ? 'Cert verified with grading service' : (certVerified.note || 'Not auto-verified')}
            {certVerified.lookup_url && (
              <a href={certVerified.lookup_url} target="_blank" rel="noopener noreferrer" className="block underline mt-0.5">
                View on grading service
              </a>
            )}
          </div>
        </div>
      )}

      {/* Photos */}
      <div className="space-y-1.5">
        <label className="block">
          <input
            data-testid="auth-photo-input"
            type="file"
            accept="image/*"
            multiple
            onChange={handleFilePick}
            className="hidden"
          />
          <span className="cursor-pointer flex items-center justify-center gap-1.5 w-full h-9 rounded-lg bg-slate-700/40 border border-dashed border-slate-600 text-xs text-slate-300 hover:bg-slate-700/60 transition-colors">
            <Camera className="w-3.5 h-3.5" />
            {totalPhotos > 0 ? `Add more (${totalPhotos}/5)` : `Upload photos${required ? ' (required)' : ''}`}
          </span>
        </label>

        {totalPhotos > 0 && (
          <div className="grid grid-cols-5 gap-1.5">
            {photoUrls?.map((url, idx) => (
              <div key={`u-${idx}`} className="relative aspect-square rounded-lg overflow-hidden bg-slate-800 border border-slate-700">
                <img src={resolveUrl(url)} alt={`upload-${idx}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  data-testid={`auth-remove-uploaded-${idx}`}
                  onClick={() => removeUploadedUrl(idx)}
                  className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/70 flex items-center justify-center"
                >
                  <X className="w-2.5 h-2.5 text-white" />
                </button>
              </div>
            ))}
            {photoFiles?.map((file, idx) => (
              <div key={`f-${idx}`} className="relative aspect-square rounded-lg overflow-hidden bg-slate-800 border border-amber-500/40">
                <img src={URL.createObjectURL(file)} alt={`staged-${idx}`} className="w-full h-full object-cover" />
                <button
                  type="button"
                  data-testid={`auth-remove-staged-${idx}`}
                  onClick={() => removeStagedFile(idx)}
                  className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/70 flex items-center justify-center"
                >
                  <X className="w-2.5 h-2.5 text-white" />
                </button>
                <span className="absolute bottom-0 left-0 right-0 bg-amber-500/80 text-[8px] text-black font-bold text-center">
                  PENDING
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <p className="text-[9px] text-slate-500 leading-snug">
        <Upload className="inline w-2.5 h-2.5 mr-0.5" />
        Front, back & close-up shots build buyer trust. Cert number unlocks <span className="text-emerald-400">verified</span> badge.
      </p>
    </div>
  );
}

/**
 * Helper: upload all staged photo files, returns combined urls.
 */
export async function uploadPhotos(photoFiles = [], existingUrls = []) {
  const urls = [...existingUrls];
  for (const file of photoFiles) {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(`${API}/upload/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    urls.push(res.data.url);
  }
  return urls;
}

export default AuthenticityFields;
